import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import DeploymentOptions from '../components/DeploymentOptions';
import html2pdf from 'html2pdf.js';
import { webSearch } from '../utils/webSearch';
import { API_URL } from '../utils/env';

export function LessonPlanCreator() {
  const [contentInput, setContentInput] = useState('');
  const [subject, setSubject] = useState('math');
  const [gradeLevel, setGradeLevel] = useState('elementary');
  const [duration, setDuration] = useState('60');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [includeActivities, setIncludeActivities] = useState(true);
  const [selectedModalities, setSelectedModalities] = useState(['visual']);
  const [includeWorksheet, setIncludeWorksheet] = useState(true);
  const [includeExamples, setIncludeExamples] = useState(true);
  const [includeScriptedLanguage, setIncludeScriptedLanguage] = useState(true);

  const subjectOptions = [
    { value: 'math', label: 'Mathematics' },
    { value: 'science', label: 'Science' },
    { value: 'english', label: 'English/Language Arts' },
    { value: 'history', label: 'History/Social Studies' },
    { value: 'art', label: 'Art' },
    { value: 'music', label: 'Music' },
    { value: 'pe', label: 'Physical Education' },
    { value: 'technology', label: 'Technology' }
  ];

  const gradeLevelOptions = [
    { value: 'elementary', label: 'Elementary (K-5)' },
    { value: 'middle', label: 'Middle School (6-8)' },
    { value: 'high', label: 'High School (9-12)' },
    { value: 'college', label: 'College/University' }
  ];

  const durationOptions = [
    { value: '30', label: '30 Minutes' },
    { value: '45', label: '45 Minutes' },
    { value: '60', label: '60 Minutes' },
    { value: '90', label: '90 Minutes' }
  ];

  const quickStartGuides = [
    {
      title: "Lesson Structure",
      description: "Define clear objectives and activities for your lesson.",
      keywords: ["Objectives", "Activities", "Assessment"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      title: "Student Engagement",
      description: "Create interactive and engaging learning experiences.",
      keywords: ["Activities", "Participation", "Interaction"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: "Assessment Methods",
      description: "Design effective ways to measure student learning.",
      keywords: ["Evaluation", "Feedback", "Progress"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Worksheet Resources",
      description: "Include downloadable worksheets to reinforce learning.",
      keywords: ["Printables", "Practice", "Reinforcement"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Concrete Examples",
      description: "Add illustrative examples to clarify key concepts.",
      keywords: ["Illustrations", "Demonstrations", "Clarity"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      title: "Teacher Scripts",
      description: "Include scripted language to guide lesson delivery.",
      keywords: ["Dialogue", "Instructions", "Phrasing"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      )
    },
    {
      title: "Learning Modalities",
      description: "Customize content for different learning styles.",
      keywords: ["Visual", "Auditory", "Kinesthetic", "Reading/Writing"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setContentInput(transcribedText);
  };

  const validateLink = async (title, url) => {
    try {
      // Make sure URL is valid
      new URL(url);
      
      // Make direct API request to validate link
      const response = await fetch(`${API_URL}/api/validate-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          links: [{ title, url }]
        })
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const validationResult = data.validationResults?.[0];
      
      return validationResult?.valid ? url : null;
    } catch (error) {
      console.error('Error validating link:', error);
      return null;
    }
  };

  const generateContent = async () => {
    if (!contentInput.trim()) {
      toast.error('Please provide lesson details');
      return;
    }

    setIsGenerating(true);
    try {
      // Handle multiple modalities
      let modalitiesText = '';
      if (selectedModalities.length > 0) {
        modalitiesText = `\nInclude the following learning modalities in your lesson plan:`;
        
        if (selectedModalities.includes('visual')) {
          modalitiesText += `\n- Visual learning: include visual aids, diagrams, and links to relevant educational videos. When suggesting videos or resources, format links as [title||url] (e.g. [Khan Academy Algebra||https://www.khanacademy.org/math/algebra])`;
        }
        if (selectedModalities.includes('auditory')) {
          modalitiesText += `\n- Auditory learning: include discussion prompts, sound-based activities, or audio resources.`;
        }
        if (selectedModalities.includes('kinesthetic')) {
          modalitiesText += `\n- Kinesthetic learning: include hands-on activities, physical movements, or manipulative tasks.`;
        }
        if (selectedModalities.includes('reading')) {
          modalitiesText += `\n- Reading/Writing: include text-based activities, written assignments, or note-taking exercises.`;
        }
      }

      // Additional features text
      let additionalFeaturesText = '';
      
      if (includeWorksheet) {
        additionalFeaturesText += `\n\nWORKSHEET SECTION:
- Create a brief description of a worksheet that reinforces the lesson concepts
- Include 2-3 free downloadable worksheets from reputable educational websites (e.g., Teachers Pay Teachers, Education.com, CommonCore Sheets)
- Format links as [Worksheet Title||URL]
- Label this section "Worksheet Resources"`;
      }
      
      if (includeExamples) {
        additionalFeaturesText += `\n\nEXAMPLES:
- Include 2-3 concrete examples throughout the lesson plan to illustrate key concepts
- Make sure these examples are grade-appropriate and directly related to the lesson objectives
- Label each example clearly as "EXAMPLE: [brief description]"
- Include real-world applications and connections
- If linking to external resources, ensure they are from reputable educational websites`;
      }
      
      if (includeScriptedLanguage) {
        additionalFeaturesText += `\n\nSCRIPTED LANGUAGE:
- Include suggested phrases, questions, and explanations that teachers can use verbatim
- Format all scripted language as "TEACHER SCRIPT: [the actual scripted language]"
- Include scripted language for introducing concepts, guiding discussions, and concluding activities
- Ensure language is grade-appropriate and aligned with educational best practices`;
      }

      const prompt = `Create a detailed lesson plan for: ${contentInput}
Subject: ${subject}
Grade Level: ${gradeLevel}
Duration: ${duration} minutes
Learning Styles: ${selectedModalities.join(', ')}

Format the lesson plan with the following sections using CAPITALIZED headers and double line breaks between sections:

LEARNING OBJECTIVES
[List 3-5 specific, measurable objectives using Bloom's Taxonomy]

REQUIRED MATERIALS
[List all necessary materials, resources, and preparation steps]
- Include only readily available materials
- If suggesting online resources, use reputable educational websites
- Format any links as [Resource Name||URL]

LESSON OVERVIEW
[Provide a comprehensive summary including key concepts and skills]
- Begin with an engaging hook
- List prerequisite knowledge
- Outline the lesson flow

ACTIVITIES
[Detail step-by-step activities with specific timing and transitions]
${includeActivities ? '- Include detailed interactive activities with clear instructions\n- Specify grouping strategies and differentiation options\n- Provide clear transitions between activities\n' : ''}

DEEP DIVE OPPORTUNITIES
[Provide 2-3 extension activities for advanced learning]
- Format each opportunity as "DEEP DIVE: [detailed description]"
- Include specific resources and guiding questions
- Connect to real-world applications or current research
- If including links, use reputable educational or academic sources

ASSESSMENT METHODS
[Describe formative and summative assessment strategies]
- Include specific success criteria
- Provide sample questions or rubrics
- Suggest methods for tracking progress
- Include both formal and informal assessment opportunities

DIFFERENTIATION STRATEGIES
[Detail specific modifications for different learning needs]
- Include scaffolding options
- Provide challenge extensions
- Address common misconceptions
- Consider language learners and diverse learning styles

EXTENSIONS/MODIFICATIONS
[Provide adaptations for different learning needs and enrichment]
- Include both support and challenge options
- Consider various learning modalities
- Provide alternative assessment options

${modalitiesText}

${additionalFeaturesText}

Important formatting guidelines:
1. Use clean bullet points with single dashes (-)
2. Leave clear spacing between sections (double line breaks)
3. Use clear section headers in capital letters
4. Include specific timing for each activity
5. Format deep dive opportunities distinctly
6. Ensure all links are from reputable educational websites
7. Include clear transitions between sections
${selectedModalities.includes('visual') ? '8. Include actual, working hyperlinks for visual resources' : ''}`;

      // Use the Gemini API directly
      const { content: generatedText, error } = await geminiApi.generateText(prompt, [], {
        duration,
        subject,
        gradeLevel,
        includeActivities,
        selectedModalities,
        includeWorksheet,
        includeExamples,
        includeScriptedLanguage
      });

      if (error) {
        throw new Error(error);
      }

      if (!generatedText) {
        throw new Error('No content received from the API');
      }
      
      // Extract and validate all links before formatting
      const linkMatches = [...generatedText.matchAll(/\[([^\|\]]+)\|\|([^\]]+)\]/g)];
      const validatedText = await linkMatches.reduce(async (textPromise, match) => {
        const text = await textPromise;
        const [fullMatch, title, url] = match;
        const validUrl = await validateLink(title, url);
        return validUrl ? 
          text.replace(fullMatch, `[${title}||${validUrl}]`) :
          text.replace(fullMatch, title); // Remove invalid links but keep the title
      }, Promise.resolve(generatedText));

      const formattedText = formatLessonPlan(validatedText);
      setGeneratedContent(formattedText);
      toast.success('Lesson plan generated successfully!');
    } catch (error) {
      console.error('Error generating lesson plan:', error);
      toast.error(error.message || 'Failed to generate lesson plan');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  const exportToPDF = () => {
    const content = document.querySelector('.lesson-plan-content');
    if (!content) return;

    const opt = {
      margin: [10, 15],
      filename: 'lesson-plan.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    // Add temporary PDF styling
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        .lesson-plan-content {
          font-size: 12pt;
          line-height: 1.6;
          padding: 20pt;
        }
        .lesson-plan-content h3 {
          font-size: 16pt;
          margin-top: 20pt;
          margin-bottom: 10pt;
          page-break-after: avoid;
          color: #1e40af;
        }
        .lesson-plan-content p {
          margin: 8pt 0;
        }
        .lesson-plan-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        .lesson-plan-content .flex {
          display: block !important;
          margin-bottom: 6pt;
        }
        .lesson-plan-content .flex span:first-child {
          display: inline-block;
          width: 6pt;
          height: 6pt;
          background-color: #2563eb;
          border-radius: 50%;
          margin-right: 8pt;
        }
        .lesson-plan-content .bg-gray-50,
        .lesson-plan-content .bg-blue-50 {
          background-color: #f8fafc !important;
          padding: 10pt;
          margin: 10pt 0;
          border-radius: 4pt;
          border-left: 3pt solid #2563eb;
        }
      }
    `;
    document.head.appendChild(style);

    toast.promise(
      html2pdf(content, opt),
      {
        loading: 'Generating PDF...',
        success: 'PDF downloaded successfully!',
        error: 'Failed to generate PDF'
      }
    ).finally(() => {
      // Remove temporary styling
      document.head.removeChild(style);
    });
  };

  const formatLessonPlan = (text) => {
    // Remove asterisks and replace with proper formatting
    let formatted = text.replace(/\*\*/g, '');
    
    // First ensure consistent section breaks by adding markers
    formatted = formatted.replace(
      /(LEARNING OBJECTIVES|REQUIRED MATERIALS|LESSON OVERVIEW|ACTIVITIES|ASSESSMENT METHODS|EXTENSIONS\/MODIFICATIONS|WORKSHEET RESOURCES|HOMEWORK\/FOLLOW-UP|TEACHER SCRIPT|DEEP DIVE OPPORTUNITIES|DIFFERENTIATION STRATEGIES)/g,
      '\n\n---SECTION_BREAK---\n\n$1'
    );
    
    // Clean up any excessive newlines
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    formatted = formatted.replace(/---SECTION_BREAK---\n\n/g, '---SECTION_BREAK---');
    
    // Format section headers with improved styling and spacing
    const sections = [
      'LEARNING OBJECTIVES',
      'REQUIRED MATERIALS',
      'LESSON OVERVIEW',
      'ACTIVITIES',
      'ASSESSMENT METHODS',
      'EXTENSIONS/MODIFICATIONS',
      'WORKSHEET RESOURCES',
      'HOMEWORK/FOLLOW-UP',
      'TEACHER SCRIPT',
      'DEEP DIVE OPPORTUNITIES',
      'DIFFERENTIATION STRATEGIES'
    ];
    
    sections.forEach(section => {
      const regex = new RegExp(`---SECTION_BREAK---${section}:?(?=\\n|$)`, 'g');
      formatted = formatted.replace(
        regex,
        `<div class="section-container mb-8">
          <h3 class="text-2xl font-extrabold text-gray-900 tracking-tight border-b-4 border-blue-500 pb-2 mb-6 mt-8 inline-block">${section}</h3>
          <div class="section-content">`
      );
    });
    
    // Close section divs
    formatted = formatted.replace(/---SECTION_BREAK---/g, '</div></div>');
    
    // Handle the last section
    formatted += '</div></div>';

    // Convert bracketed links to HTML anchor tags with validation
    formatted = formatted.replace(
      /\[([^\|\]]+)\|\|([^\]]+)\]/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors duration-200"><span>$1</span><svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg></a>'
    );

    // Format timing indicators with improved visibility
    formatted = formatted.replace(
      /(\d+(?:-\d+)?\s*minutes?)/gi,
      '<span class="font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">$1</span>'
    );

    // Format teacher scripts with improved styling and spacing
    formatted = formatted.replace(
      /TEACHER SCRIPT:\s*([^\n]+)/g,
      '<div class="bg-gray-50 border-l-4 border-blue-500 pl-4 py-3 rounded-r-lg mb-4"><div class="font-medium text-sm text-blue-700 mb-1">Teacher Script</div><div class="text-gray-700 italic">$1</div></div>'
    );

    // Format examples with improved styling and spacing
    formatted = formatted.replace(
      /EXAMPLE:\s*([^\n]+)/g,
      '<div class="bg-gray-50 border-l-4 border-green-500 pl-4 py-3 rounded-r-lg mb-4"><div class="font-medium text-sm text-green-700 mb-1">Example</div><div class="text-gray-700">$1</div></div>'
    );

    // Format deep dive opportunities with special styling and spacing
    formatted = formatted.replace(
      /DEEP DIVE:\s*([^\n]+)/g,
      '<div class="bg-blue-50 border-l-4 border-blue-600 pl-4 py-3 rounded-r-lg mb-4"><div class="font-medium text-sm text-blue-800 mb-1">Deep Dive Learning Opportunity</div><div class="text-blue-900">$1</div></div>'
    );

    // Format bullet points with improved spacing
    formatted = formatted.replace(
      /^-\s+(.+)$/gm,
      '<div class="flex items-start group mb-4"><span class="mr-2 mt-2 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-200"></span><span class="text-gray-700">$1</span></div>'
    );
    
    return formatted;
  };

  // Add this after the existing JSX, before the closing div
  const apiDocumentation = (
    <div className="mt-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">API Documentation</h2>
          
          <div className="space-y-6">
            {/* Endpoint Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Endpoint</h3>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                POST https://lesson-plan-api-44565368406.us-central1.run.app/api/lesson-plan
              </div>
            </div>

            {/* Request Format */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Format</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="language-json">
                  <code>{JSON.stringify({
                    subject: "mathematics",
                    gradeLevel: "elementary",
                    duration: "60",
                    includeActivities: true,
                    selectedModalities: ["visual", "kinesthetic"],
                    includeWorksheet: true,
                    includeExamples: true,
                    includeScriptedLanguage: true,
                    contentInput: "Teaching addition and subtraction with numbers 1-20"
                  }, null, 2)}</code>
                </pre>
              </div>
            </div>

            {/* Parameters */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parameter</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">subject</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                      <td className="px-6 py-4 text-sm text-gray-500">The subject area for the lesson plan</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">gradeLevel</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Target grade level (elementary, middle, high, college)</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">duration</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Lesson duration in minutes</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">includeActivities</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">boolean</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Whether to include interactive activities</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">selectedModalities</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">array</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Learning modalities to include</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">includeWorksheet</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">boolean</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Whether to include worksheet resources</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">includeExamples</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">boolean</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Whether to include example problems</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">includeScriptedLanguage</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">boolean</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Whether to include teacher scripts</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">No</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">contentInput</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">string</td>
                      <td className="px-6 py-4 text-sm text-gray-500">Specific content or topic for the lesson</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Yes</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Example cURL Request */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Example cURL Request</h3>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                {`curl -X POST "https://lesson-plan-api-44565368406.us-central1.run.app/api/lesson-plan" \\
  -H "Content-Type: application/json" \\
  -d '{
    "subject": "mathematics",
    "gradeLevel": "elementary",
    "duration": "60",
    "includeActivities": true,
    "selectedModalities": ["visual", "kinesthetic"],
    "includeWorksheet": true,
    "includeExamples": true,
    "includeScriptedLanguage": true,
    "contentInput": "Teaching addition and subtraction with numbers 1-20"
  }'`}
              </div>
            </div>

            {/* Response Format */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Format</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="language-json">
                  <code>{JSON.stringify({
                    lessonPlan: "Generated lesson plan content with all specified sections...",
                  }, null, 2)}</code>
                </pre>
              </div>
            </div>

            {/* Rate Limits */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Rate Limits & Usage</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Maximum request timeout: 300 seconds</li>
                <li>Maximum concurrent requests: 80</li>
                <li>Memory per instance: 1GB</li>
                <li>CPU: 1 core</li>
              </ul>
            </div>

            {/* Error Handling */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Responses</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">400 Bad Request</h4>
                  <pre className="bg-gray-50 rounded-lg p-4 mt-2">
                    <code>{JSON.stringify({
                      detail: {
                        error: "Validation error",
                        message: "Required field missing or invalid"
                      }
                    }, null, 2)}</code>
                  </pre>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">500 Internal Server Error</h4>
                  <pre className="bg-gray-50 rounded-lg p-4 mt-2">
                    <code>{JSON.stringify({
                      detail: {
                        error: "Failed to generate lesson plan",
                        message: "Internal server error"
                      }
                    }, null, 2)}</code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern Hero Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-500/40 via-blue-300/10 to-blue-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-400/40 via-blue-300/10 to-blue-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-800/50 border border-blue-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                <span className="text-sm text-blue-100">AI-Powered Education</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                Lesson Plan Creator
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-blue-200 max-w-xl">
                Design engaging lesson plans tailored to your teaching style
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Smart Templates</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Instant Refinements</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Target Audience Banner */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Who is this tool for?</h3>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Teachers - Create detailed, engaging lesson plans</li>
              <li>Curriculum Designers - Develop structured learning experiences</li>
              <li>Education Specialists - Design differentiated instruction</li>
            </ul>
          </div>

          {/* Content Generation Form */}
          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Lesson Settings</h2>
                <div className="space-y-6">
                  {/* Voice Recorder */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Record Lesson Content
                    </label>
                    <VoiceRecorder 
                      onTranscriptionComplete={handleTranscriptionComplete}
                      placeholder="Click to start recording your lesson content..."
                    />
                  </div>

                  {/* Content Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson Overview
                    </label>
                    <textarea
                      value={contentInput}
                      onChange={(e) => setContentInput(e.target.value)}
                      rows="4"
                      placeholder="Record audio or type your lesson details..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Subject and Grade Level */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {subjectOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setSubject(option.value)}
                            className={`flex items-center justify-center px-3 py-2 rounded-lg border text-sm ${
                              subject === option.value
                                ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grade Level
                      </label>
                      <div className="grid grid-cols-1 gap-2">
                        {gradeLevelOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => setGradeLevel(option.value)}
                            className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                              gradeLevel === option.value
                                ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {durationOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setDuration(option.value)}
                          className={`flex items-center justify-center px-3 py-2 rounded-lg border text-sm ${
                            duration === option.value
                              ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Learning Modalities Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Learning Modalities to Include
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { id: 'visual', label: 'Visual' },
                        { id: 'auditory', label: 'Auditory' },
                        { id: 'kinesthetic', label: 'Kinesthetic' },
                        { id: 'reading', label: 'Reading/Writing' }
                      ].map(modality => (
                        <button
                          key={modality.id}
                          onClick={() => {
                            if (selectedModalities.includes(modality.id)) {
                              setSelectedModalities(selectedModalities.filter(id => id !== modality.id));
                            } else {
                              setSelectedModalities([...selectedModalities, modality.id]);
                            }
                          }}
                          className={`flex items-center justify-center px-4 py-3 rounded-lg border ${
                            selectedModalities.includes(modality.id)
                              ? 'bg-blue-50 border-blue-500 text-blue-700'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {modality.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Additional Features */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button
                      onClick={() => setIncludeWorksheet(!includeWorksheet)}
                      className={`px-4 py-3 rounded-lg border ${
                        includeWorksheet
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      Worksheets
                    </button>
                    <button
                      onClick={() => setIncludeExamples(!includeExamples)}
                      className={`px-4 py-3 rounded-lg border ${
                        includeExamples
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      Examples
                    </button>
                    <button
                      onClick={() => setIncludeScriptedLanguage(!includeScriptedLanguage)}
                      className={`px-4 py-3 rounded-lg border ${
                        includeScriptedLanguage
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      Teacher Scripts
                    </button>
                  </div>

                  <button
                    onClick={generateContent}
                    disabled={isGenerating}
                    className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                      isGenerating
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isGenerating ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating Lesson Plan...
                      </div>
                    ) : (
                      'Generate Lesson Plan'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Generated Content */}
          {generatedContent ? (
            <div className="mt-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">Generated Lesson Plan</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => copyToClipboard(generatedContent)}
                        className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy to Clipboard
                      </button>
                      <button
                        onClick={exportToPDF}
                        className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download PDF
                      </button>
                    </div>
                  </div>
                  <div className="prose prose-blue max-w-none">
                    <div 
                      className="lesson-plan-content bg-white rounded-lg"
                      dangerouslySetInnerHTML={{ __html: generatedContent }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No lesson plan generated yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Fill in the lesson details and click generate to create your plan.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add API Documentation */}
      {apiDocumentation}

      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="lesson-plan-creator" position="none" />
    </div>
  );
}

export default LessonPlanCreator; 